/**
 * Which project an expense is filed in — a select on the expense sheet,
 * "No project" by default.
 *
 * Only open projects are offered: an archived one is locked. A correction to
 * an expense that is *in* an archived project gets no control at all, only a
 * line saying where it is, because the server will not move it out of one and
 * a picker that can only be refused is worse than none. `projectPicker` in
 * `src/lib/projects.ts` decides which of the two this is.
 *
 * Drawn only once the group has at least one project to offer; a select whose
 * only option is "No project" is one more thing to read on the sheet people
 * fill in most.
 */

import type { Project } from '../api'
import { projectPicker } from '../lib/projects'

export function ProjectPicker({
  id,
  projects,
  currentId,
  value,
  onChange,
  label = 'Project',
}: {
  id: string
  /** What the select is called — "For project" on the Settle up dialog. */
  label?: string
  projects: readonly Project[]
  /** The project the expense being corrected is in now; `null` on a new one. */
  currentId: string | null
  value: string | null
  onChange: (projectId: string | null) => void
}) {
  const { options, locked } = projectPicker(projects, currentId)

  if (locked) {
    return (
      <div className="form__row">
        <span className="sheet__label">Project</span>
        <p className="muted note">
          In {locked.name}, which is archived. Unarchive it to move this expense out of it.
        </p>
      </div>
    )
  }

  if (options.length === 0) return null

  return (
    <div className="form__row form__row--narrow">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={value !== null && options.some((option) => option.id === value) ? value : ''}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      >
        <option value="">No project</option>
        {options.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
    </div>
  )
}
